class BundleBuilder extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialised === 'true') return;
    this.dataset.initialised = 'true';

    this.items = [...this.querySelectorAll('[data-item]')];
    if (!this.items.length) return;
    this.stage = this.querySelector('[data-stage]');
    this.ring = this.querySelector('[data-ring]');
    this.jumps = [...this.querySelectorAll('[data-jump]')];
    this.qty = this.items.map(() => 0);
    this.active = 0;
    this.position = 0;

    this.el = (sel) => this.querySelector(sel);
    this.dAge = this.el('[data-d-age]');
    this.dNote = this.el('[data-d-note]');
    this.dTitle = this.el('[data-d-title]');
    this.dLink = this.el('[data-d-link]');
    this.dPrice = this.el('[data-d-price]');
    this.dQty = this.el('[data-d-qty]');
    this.dStepper = this.el('[data-d-stepper]');
    this.dSoldout = this.el('[data-d-soldout]');
    this.shelf = this.el('[data-shelf]');
    this.tierEl = this.el('[data-tier]');
    this.meterFill = this.el('[data-meter-fill]');
    this.marks = [...this.querySelectorAll('[data-mark]')];
    this.barCount = this.el('[data-bar-count]');
    this.saveRow = this.el('[data-save-row]');
    this.saveLabel = this.el('[data-save-label]');
    this.saveEl = this.el('[data-save]');
    this.subtotalEl = this.el('[data-subtotal]');
    this.totalEl = this.el('[data-total]');
    this.addButton = this.el('[data-add]');
    this.addLabel = this.el('[data-add-label]');
    this.statusEl = this.el('[data-status]');

    try {
      this.tiers = JSON.parse(this.dataset.tiers || '[]').sort((a, b) => a.qty - b.qty);
    } catch (error) {
      this.tiers = [];
    }
    this.maxTierQty = this.tiers.length ? this.tiers[this.tiers.length - 1].qty : 0;
    this.money = new Intl.NumberFormat('en-GB', { style: 'currency', currency: window.Shopify?.currency?.active || 'GBP' });
    this.defaultLabel = this.addLabel?.textContent || 'Add bundle to bag';
    this.emptyMessage = this.tierEl?.textContent || '';
    this.emptyBar = this.barCount?.textContent || '';
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.info = this.items.map((item) => {
      try {
        return JSON.parse(item.querySelector('script[data-info]')?.textContent || '{}');
      } catch (error) {
        return {};
      }
    });
    this.featuresEl = this.el('[data-features]');
    this.notesEl = this.el('[data-notes]');
    this.tastingEl = this.el('[data-tasting]');
    this.moreBtn = this.el('[data-more]');
    this.moreLabel = this.el('[data-more-label]');
    this.moreBtn?.addEventListener('click', () => this.toggleNotes());

    this.bindCarousel();
    this.dStepper?.addEventListener('click', (event) => {
      const step = event.target.closest('[data-step]');
      if (step) this.adjust(this.active, Number(step.dataset.step));
    });
    this.shelf?.addEventListener('click', (event) => {
      const unit = event.target.closest('[data-unit]');
      if (unit) this.adjust(Number(unit.dataset.unit), -1);
    });
    this.addButton?.addEventListener('click', () => this.submit());

    this.layout(0);
    this.showDetail();
    this.render();
    requestAnimationFrame(() => this.classList.add('is-ready'));
  }

  /* ---------- carousel ---------- */

  wrapOffset(index, position) {
    const n = this.items.length;
    let d = (index - position) % n;
    if (d > n / 2) d -= n;
    if (d < -n / 2) d += n;
    return d;
  }

  spreadPx() {
    return (this.items[0]?.offsetWidth || 360) * 0.66;
  }

  layout(position) {
    const spread = this.spreadPx();
    this.items.forEach((item, i) => {
      const d = this.wrapOffset(i, position);
      const a = Math.abs(d);
      const x = d * spread * (1 - Math.min(a, 2.5) * 0.08);
      const z = -a * 220;
      const rot = Math.max(-60, Math.min(60, -d * 32));
      const scale = 1 - Math.min(a, 3) * 0.12;
      item.style.transform = `translate3d(${x}px, 0, ${z}px) rotateY(${rot}deg) scale(${scale})`;
      item.style.opacity = a > 2.4 ? '0' : String(1 - a * 0.3);
      item.style.zIndex = String(100 - Math.round(a * 10));
      item.style.filter = `brightness(${1 - Math.min(a, 2) * 0.28})`;
      item.style.pointerEvents = a > 2.4 ? 'none' : 'auto';
      item.classList.toggle('is-front', a < 0.5);
      item.setAttribute('aria-current', a < 0.5 ? 'true' : 'false');
    });
  }

  goTo(index, animate = true) {
    const n = this.items.length;
    const target = ((index % n) + n) % n;
    this.active = target;
    this.position = target;
    this.ring.classList.toggle('is-dragging', !animate);
    this.layout(target);
    this.showDetail();
  }

  bindCarousel() {
    this.el('[data-prev]')?.addEventListener('click', () => this.goTo(this.active - 1));
    this.el('[data-next]')?.addEventListener('click', () => this.goTo(this.active + 1));
    this.jumps.forEach((btn) => btn.addEventListener('click', () => this.goTo(Number(btn.dataset.jump))));

    this.items.forEach((item, i) => {
      item.addEventListener('click', () => {
        if (this.dragMoved) return;
        if (i === this.active) this.adjust(i, 1);
        else this.goTo(i);
      });
    });

    this.stage.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.goTo(this.active - 1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); this.goTo(this.active + 1); }
      if (event.key === '+' || event.key === '=') this.adjust(this.active, 1);
      if (event.key === '-') this.adjust(this.active, -1);
    });

    let startX = 0;
    let startPos = 0;
    let pointerId = null;

    this.stage.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || event.target.closest('[data-prev],[data-next]')) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startPos = this.position;
      this.dragMoved = false;
    });
    window.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      const dx = event.clientX - startX;
      if (!this.dragMoved && Math.abs(dx) < 6) return;
      if (!this.dragMoved) {
        this.dragMoved = true;
        this.ring.classList.add('is-dragging');
        this.stage.setPointerCapture?.(pointerId);
      }
      this.position = startPos - dx / this.spreadPx();
      this.layout(this.position);
    });
    const end = (event) => {
      if (event.pointerId !== pointerId) return;
      pointerId = null;
      if (this.dragMoved) {
        this.ring.classList.remove('is-dragging');
        this.goTo(Math.round(this.position));
        window.setTimeout(() => { this.dragMoved = false; }, 0);
      }
    };
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);

    window.addEventListener('resize', () => this.layout(this.position));
  }

  showDetail() {
    const item = this.items[this.active];
    const d = item.dataset;
    this.style.setProperty('--accent', d.accent);
    this.jumps.forEach((btn) => btn.setAttribute('aria-selected', String(Number(btn.dataset.jump) === this.active)));

    const detail = this.el('[data-detail]');
    detail?.classList.remove('is-swapping');
    void detail?.offsetWidth;
    detail?.classList.add('is-swapping');

    if (this.dAge) this.dAge.innerHTML = d.age ? `<span>${d.age}</span><small>years</small>` : '<span class="is-box">3×5cl</span>';
    if (this.dNote) this.dNote.textContent = d.note || '';
    if (this.dTitle) this.dTitle.textContent = d.title;
    if (this.dLink) this.dLink.href = d.url;
    if (this.dPrice) this.dPrice.textContent = d.tierItem === 'true' ? d.priceLabel : `${d.priceLabel} · not in volume tiers`;
    const available = d.available === 'true';
    if (this.dStepper) this.dStepper.hidden = !available;
    if (this.dSoldout) this.dSoldout.hidden = available;
    if (this.dQty) this.dQty.textContent = String(this.qty[this.active]);
    this.renderInfo();
  }

  /* ---------- key features & tasting notes ---------- */

  static icon(name) {
    const paths = {
      age: '<path d="M7 3h10M7 21h10M8 3c0 5 8 6 8 9s-8 4-8 9M16 3c0 5-8 6-8 9s8 4 8 9"/>',
      abv: '<path d="M12 3s6 7 6 11a6 6 0 0 1-12 0c0-4 6-11 6-11z"/>',
      volume: '<path d="M10 2h4v4l1.5 2.5V21h-7V8.5L10 6z"/><path d="M8.5 13h7"/>',
      cask: '<ellipse cx="12" cy="12" rx="7" ry="9"/><path d="M5.5 8h13M5.5 16h13M12 3v18"/>',
      origin: '<path d="M12 21s7-6.5 7-12a7 7 0 0 0-14 0c0 5.5 7 12 7 12z"/><circle cx="12" cy="9" r="2.5"/>',
      year: '<rect x="4" y="5" width="16" height="15" rx="1.5"/><path d="M4 10h16M9 3v4M15 3v4"/>',
      star: '<path d="M12 3l2.6 5.6 6 .7-4.5 4.1 1.2 6L12 16.5 6.7 19.4l1.2-6L3.4 9.3l6-.7z"/>'
    };
    return `<svg viewBox="0 0 24 24" aria-hidden="true">${paths[name] || paths.star}</svg>`;
  }

  esc(value) {
    return String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  featureList(info) {
    if (info.features) {
      return info.features.split(',').map((f) => f.trim()).filter(Boolean).map((value) => ({ icon: 'star', label: '', value }));
    }
    const list = [];
    if (info.age) list.push({ icon: 'age', label: 'Aged', value: `${info.age} years` });
    if (info.abv) list.push({ icon: 'abv', label: 'Strength', value: info.abv });
    if (info.volume) list.push({ icon: 'volume', label: 'Bottle', value: info.volume });
    if (info.cask) list.push({ icon: 'cask', label: 'Cask', value: info.cask });
    if (info.year) list.push({ icon: 'year', label: 'Distilled', value: String(info.year) });
    const origin = [info.region, info.distillery].filter(Boolean).join(' · ');
    if (origin) list.push({ icon: 'origin', label: 'Origin', value: origin });
    return list;
  }

  renderInfo() {
    const info = this.info?.[this.active] || {};
    const item = this.items[this.active];

    if (this.featuresEl) {
      this.featuresEl.innerHTML = this.featureList(info)
        .map((f) => `<li>${BundleBuilder.icon(f.icon)}<span>${f.label ? `<small>${this.esc(f.label)}</small>` : ''}${this.esc(f.value)}</span></li>`)
        .join('');
      this.featuresEl.classList.remove('is-swapping');
      void this.featuresEl.offsetWidth;
      this.featuresEl.classList.add('is-swapping');
    }

    const notes = [['Nose', info.nose], ['Palate', info.palate], ['Finish', info.finish]].filter((n) => n[1]);

    if (this.tastingEl) {
      const parts = [];
      if (info.short) parts.push(`<p class="rdi-bb__notes-lead">${this.esc(info.short)}</p>`);
      if (notes.length) {
        parts.push(`<dl class="rdi-bb__tasting">${notes.map(([k, v]) => `<div><dt>${k}</dt><dd>${this.esc(v)}</dd></div>`).join('')}</dl>`);
      }
      this.tastingEl.innerHTML = parts.join('');
      this.tastingEl.hidden = parts.length === 0;
      this.tastingEl.classList.remove('is-swapping');
      void this.tastingEl.offsetWidth;
      this.tastingEl.classList.add('is-swapping');
    }

    if (this.notesEl) {
      const parts = [];
      if (info.description && info.description !== info.short) parts.push(`<p class="rdi-bb__notes-copy">${this.esc(info.description)}</p>`);
      parts.push(`<a class="rdi-bb__notes-link" href="${this.esc(item.dataset.url)}">View the full ${this.esc(item.dataset.title)} page &rarr;</a>`);
      this.notesEl.innerHTML = parts.join('');
    }
  }

  toggleNotes() {
    const open = this.moreBtn.getAttribute('aria-expanded') !== 'true';
    this.moreBtn.setAttribute('aria-expanded', String(open));
    this.notesEl.hidden = !open;
    if (this.moreLabel) this.moreLabel.textContent = open ? 'Show less' : 'Learn more';
  }

  /* ---------- selection & pricing ---------- */

  adjust(index, delta) {
    const item = this.items[index];
    if (!item || item.dataset.available !== 'true') return;
    const next = Math.min(12, Math.max(0, this.qty[index] + delta));
    if (next === this.qty[index]) return;
    this.qty[index] = next;
    item.classList.toggle('is-selected', next > 0);
    const badge = item.querySelector('[data-badge]');
    if (badge) {
      badge.textContent = String(next);
      badge.classList.remove('is-bumped');
      void badge.offsetWidth;
      if (delta > 0) badge.classList.add('is-bumped');
    }
    if (index === this.active && this.dQty) this.dQty.textContent = String(next);
    this.render(delta > 0 ? index : null);
  }

  lines() {
    return this.items
      .map((item, i) => ({
        index: i,
        id: Number(item.dataset.variantId),
        title: item.dataset.title,
        thumb: item.dataset.thumb,
        accent: item.dataset.accent,
        priceCents: Number(item.dataset.priceCents),
        tierItem: item.dataset.tierItem === 'true',
        quantity: this.qty[i]
      }))
      .filter((line) => line.quantity > 0);
  }

  activeTier(bottles) {
    let active = null;
    for (const tier of this.tiers) if (bottles >= tier.qty) active = tier;
    return active;
  }

  render(addedIndex = null) {
    const lines = this.lines();
    const count = lines.reduce((s, l) => s + l.quantity, 0);
    const bottles = lines.filter((l) => l.tierItem).reduce((s, l) => s + l.quantity, 0);
    const boxes = count - bottles;
    const subtotal = lines.reduce((s, l) => s + l.priceCents * l.quantity, 0);
    const tier = this.activeTier(bottles);
    // Per-unit floor rounding matches Shopify's checkout allocation.
    const saving = tier
      ? lines.filter((l) => l.tierItem).reduce((s, l) => s + Math.floor((l.priceCents * tier.pct) / 100) * l.quantity, 0)
      : 0;
    const total = subtotal - saving;

    this.classList.toggle('has-selection', count > 0);

    if (this.shelf) {
      const units = [];
      lines.forEach((l) => {
        for (let k = 0; k < l.quantity; k += 1) units.push(l);
      });
      this.shelf.innerHTML = units
        .map((u, k) => {
          const isNew = addedIndex === u.index && k === units.map((x) => x.index).lastIndexOf(u.index);
          return `<li><button type="button" class="rdi-bb__unit${isNew ? ' is-new' : ''}" data-unit="${u.index}" style="--unit-accent:${u.accent}" aria-label="Remove one ${u.title}"><img src="${u.thumb}" alt="" width="90" height="120"></button></li>`;
        })
        .join('');
    }

    if (this.meterFill && this.maxTierQty) this.meterFill.style.width = `${Math.min(100, (bottles / this.maxTierQty) * 100)}%`;
    this.marks.forEach((m) => m.classList.toggle('is-hit', bottles >= Number(m.dataset.mark)));

    if (this.tierEl) {
      let msg = this.emptyMessage;
      if (count > 0) {
        const next = this.tiers.find((t) => bottles < t.qty);
        const more = next ? next.qty - bottles : 0;
        const s = more > 1 ? 's' : '';
        if (tier && next) msg = `${tier.pct}% off unlocked — ${more} more bottle${s} for ${next.pct}% off.`;
        else if (tier) msg = `${tier.pct}% off unlocked — ${this.dataset.topMessage || 'the full ladder.'}`;
        else if (next) msg = `Add ${more} more bottle${s} to unlock ${next.pct}% off.`;
      }
      this.tierEl.textContent = msg;
    }

    if (this.barCount) {
      const parts = [];
      if (bottles) parts.push(`${bottles} bottle${bottles > 1 ? 's' : ''}`);
      if (boxes) parts.push(`${boxes} gift box${boxes > 1 ? 'es' : ''}`);
      this.barCount.textContent = parts.length ? parts.join(' · ') : this.emptyBar;
    }
    if (this.saveRow) this.saveRow.hidden = saving === 0;
    if (this.saveLabel && tier) this.saveLabel.textContent = `${tier.pct}% cellar discount`;
    if (this.saveEl) this.saveEl.textContent = `−${this.money.format(saving / 100)}`;
    if (this.subtotalEl) {
      this.subtotalEl.hidden = saving === 0;
      this.subtotalEl.textContent = this.money.format(subtotal / 100);
    }
    this.animateTotal(count ? total : 0);

    if (this.addButton) this.addButton.disabled = count === 0;
    if (this.addLabel && !this.busy) this.addLabel.textContent = count > 1 ? `${this.defaultLabel} · ${count}` : this.defaultLabel;
  }

  animateTotal(to) {
    if (!this.totalEl) return;
    const from = this.shownTotal || 0;
    this.shownTotal = to;
    if (!to) { this.totalEl.textContent = '—'; return; }
    if (this.reduceMotion || !from) { this.totalEl.textContent = this.money.format(to / 100); return; }
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / 380);
      const e = 1 - Math.pow(1 - t, 3);
      this.totalEl.textContent = this.money.format((from + (to - from) * e) / 100);
      if (t < 1 && this.shownTotal === to) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  /* ---------- cart ---------- */

  async submit() {
    const lines = this.lines();
    if (!lines.length || this.busy) return;
    this.busy = true;
    this.addButton.disabled = true;
    this.addButton.setAttribute('aria-busy', 'true');
    if (this.addLabel) this.addLabel.textContent = 'Adding…';
    if (this.statusEl) this.statusEl.textContent = 'Adding your bundle to the bag.';

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: lines.map((l) => ({ id: l.id, quantity: l.quantity })) })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.description || 'Your bundle could not be added. Please try again.');

      if (this.statusEl) this.statusEl.textContent = 'Your bundle has been added to the bag.';
      this.qty = this.items.map(() => 0);
      this.items.forEach((item) => {
        item.classList.remove('is-selected');
        const badge = item.querySelector('[data-badge]');
        if (badge) badge.textContent = '0';
      });
      if (this.dQty) this.dQty.textContent = '0';
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item: result.items ? result.items[0] : result } }));
    } catch (error) {
      if (this.statusEl) this.statusEl.textContent = error.message;
      if (this.addLabel) this.addLabel.textContent = 'Try again';
      window.setTimeout(() => this.render(), 2600);
    } finally {
      this.busy = false;
      this.addButton.removeAttribute('aria-busy');
      this.render();
    }
  }
}

if (!customElements.get('bundle-builder')) customElements.define('bundle-builder', BundleBuilder);
